// src/modules/Careers/index.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../config/supabase';
import { useTranslation } from '../../contexts/LanguageContext';
import { FaMapMarkerAlt, FaBriefcase, FaClock, FaLaptopHouse, FaArrowRight, FaSearch, FaFilter } from 'react-icons/fa';

// Styled Components
const CareersContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #111 0%, #2a2a2a 100%);
  padding: 120px 24px 80px;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  color: white;
  margin-bottom: 16px;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 24px;
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 280px;

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
  }

  input {
    width: 100%;
    padding: 14px 16px 14px 44px;
    background: white;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 15px;
    transition: all 200ms;

    &:focus {
      outline: none;
      border-color: #111;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }

    &::placeholder {
      color: #999;
    }
  }
`;

const FilterSelect = styled.select`
  padding: 14px 40px 14px 16px;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: #111;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const JobsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const JobCard = styled(Link)`
  display: block;
  background: white;
  border-radius: 16px;
  padding: 28px;
  text-decoration: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 300ms;
  border: 1px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
    border-color: #111;
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
`;

const JobTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  line-height: 1.3;
  margin: 0;
`;

const JobDepartment = styled.span`
  display: inline-block;
  padding: 6px 12px;
  background: #f5f5f5;
  color: #666;
  font-size: 12px;
  font-weight: 500;
  border-radius: 20px;
  white-space: nowrap;
`;

const JobMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;

  svg {
    font-size: 14px;
    color: #999;
  }
`;

const JobExcerpt = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ApplyLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #111;
  transition: gap 200ms;

  ${JobCard}:hover & {
    gap: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  grid-column: 1 / -1;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  font-size: 15px;
  color: #666;
  max-width: 400px;
  margin: 0 auto;
`;

const LoadingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const LoadingLine = styled.div`
  height: ${props => props.$height || '16px'};
  width: ${props => props.$width || '100%'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: ${props => props.$mb || '12px'};

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const StatsSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 60px;
  margin-top: 40px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 30px;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const Careers = () => {
  const { t, language } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterModality, setFilterModality] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        // Se a tabela não existir, só loga e continua com array vazio
        console.warn('Erro ao buscar vagas (tabela pode não existir):', error.message);
        setJobs([]);
        return;
      }
      setJobs(data || []);
    } catch (error) {
      console.error('Erro ao buscar vagas:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtros
  const departments = [...new Set(jobs.map(job => job.department))];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || job.department === filterDepartment;
    const matchesModality = filterModality === 'all' || job.modality === filterModality;
    return matchesSearch && matchesDepartment && matchesModality;
  });

  // Helper para obter conteúdo traduzido
  const getTranslatedField = (job, field) => {
    if (language === 'en' && job[`${field}_en`]) {
      return job[`${field}_en`];
    }
    if (language === 'es' && job[`${field}_es`]) {
      return job[`${field}_es`];
    }
    return job[field];
  };

  const getTypeLabel = (type) => {
    const labels = {
      'full-time': t('careers.types.fullTime'),
      'part-time': t('careers.types.partTime'),
      'contract': t('careers.types.contract'),
      'internship': t('careers.types.internship'),
      'freelance': t('careers.types.freelance'),
    };
    return labels[type] || type;
  };

  const getModalityLabel = (modality) => {
    const labels = {
      'on-site': t('careers.modalities.onSite'),
      'remote': t('careers.modalities.remote'),
      'hybrid': t('careers.modalities.hybrid'),
    };
    return labels[modality] || modality;
  };

  const getExcerpt = (description, maxLength = 120) => {
    if (!description) return '';
    const stripped = description.replace(/[#*`_\[\]]/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength).trim() + '...';
  };

  return (
    <CareersContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>{t('careers.pageTitle')}</HeroTitle>
          <HeroSubtitle>{t('careers.pageSubtitle')}</HeroSubtitle>

          <StatsSection>
            <StatItem>
              <StatNumber>{jobs.length}</StatNumber>
              <StatLabel>{t('careers.openPositions')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{departments.length}</StatNumber>
              <StatLabel>{t('careers.departments')}</StatLabel>
            </StatItem>
          </StatsSection>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <FiltersBar>
          <SearchBox>
            <FaSearch />
            <input
              type="text"
              placeholder={t('careers.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>

          <FilterSelect
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
          >
            <option value="all">{t('careers.allDepartments')}</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filterModality}
            onChange={(e) => setFilterModality(e.target.value)}
          >
            <option value="all">{t('careers.allModalities')}</option>
            <option value="on-site">{t('careers.modalities.onSite')}</option>
            <option value="remote">{t('careers.modalities.remote')}</option>
            <option value="hybrid">{t('careers.modalities.hybrid')}</option>
          </FilterSelect>
        </FiltersBar>

        {loading ? (
          <LoadingGrid>
            {[1, 2, 3, 4].map(i => (
              <LoadingCard key={i}>
                <LoadingLine $height="24px" $width="70%" />
                <LoadingLine $height="14px" $width="40%" />
                <LoadingLine $height="14px" $width="90%" $mb="8px" />
                <LoadingLine $height="14px" $width="80%" />
              </LoadingCard>
            ))}
          </LoadingGrid>
        ) : filteredJobs.length === 0 ? (
          <EmptyState>
            <EmptyTitle>{t('careers.noJobsTitle')}</EmptyTitle>
            <EmptyText>
              {jobs.length === 0
                ? t('careers.noJobsText')
                : t('careers.noJobsFiltered')
              }
            </EmptyText>
          </EmptyState>
        ) : (
          <JobsGrid>
            {filteredJobs.map(job => (
              <JobCard key={job.id} to={`/carreiras/${job.slug}`}>
                <JobHeader>
                  <JobTitle>{getTranslatedField(job, 'title')}</JobTitle>
                  <JobDepartment>{job.department}</JobDepartment>
                </JobHeader>

                <JobMeta>
                  <MetaItem>
                    <FaMapMarkerAlt />
                    {job.location}
                  </MetaItem>
                  <MetaItem>
                    <FaBriefcase />
                    {getTypeLabel(job.type)}
                  </MetaItem>
                  <MetaItem>
                    <FaLaptopHouse />
                    {getModalityLabel(job.modality)}
                  </MetaItem>
                </JobMeta>

                <JobExcerpt>
                  {getExcerpt(getTranslatedField(job, 'description'))}
                </JobExcerpt>

                <ApplyLink>
                  {t('careers.viewJob')} <FaArrowRight />
                </ApplyLink>
              </JobCard>
            ))}
          </JobsGrid>
        )}
      </ContentSection>
    </CareersContainer>
  );
};

export default Careers;
